<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <b-card no-body>
    <b-overlay :show="loading" rounded="sm">
      <b-table :items="users" :fields="fields" responsive>
        <template #cell(company_name)="data">
          {{ (data.item.customer && data.item.customer.company_name) || '—' }}
        </template>

        <template #cell(role_name)="data">
          <b-badge :variant="data.value | roleColor">
            {{ data.value | roleName }}
          </b-badge>
        </template>

        <template #cell(scope)="data">
          <div v-if="uniqueScopedRoles(data.item).length">
            <span
              v-for="scope in uniqueScopedRoles(data.item)"
              :key="`${scope.role_resource_type}-${scope.role_resource_id}`"
              class="mr-1"
            >
              <router-link
                v-if="scope.role_resource_type === 'Customer'"
                :to="{ name: 'projects', params: { customerId: scope.role_resource_id } }"
                class="text-primary font-weight-bolder"
              >
                {{ scope.role_resource_name }}
              </router-link>
              <span v-else>
                {{ scope.role_resource_name }}
              </span>
            </span>
          </div>
          <span v-else>—</span>
        </template>

        <template #cell(created_at)="data">
          {{ data.value | date }}
        </template>

        <template #cell(tools)="data">
          <b-dropdown
            id="dropdown-1"
            size="sm"
            dropright
            no-caret
            variant="outline-none"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="18" color="#000" />
            </template>
            <b-dropdown-item
              v-b-modal="`edit-user-${data.item.id}`"
              @click="openEditUser(data.item)"
            >
              {{ $t('edit') }}
            </b-dropdown-item>
            <b-dropdown-item @click="deactivate(data.item)">
              <span
                :class="
                  data.item.active_status ? 'text-danger' : 'text-success'
                "
              >
                {{
                  data.item.active_status ? $t('deactivate') : $t('reactivate')
                }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.role_name === 'customer'"
              @click="removeUser(data.item)"
            >
              <span class="text-danger">Eliminar</span>
            </b-dropdown-item>
            <b-modal
              :id="`edit-user-${data.item.id}`"
              title="Editar usuario"
              hide-footer
            >
              <b-form>
                <b-form-group label="Nombre">
                  <b-form-input
                    v-model="selectedUser.name"
                    autocomplete="off"
                    type="text"
                  />
                  <small
                    v-if="selectedUserErrors.name"
                    class="text-danger"
                  >
                    {{ selectedUserErrors.name }}
                  </small>
                </b-form-group>
                <b-form-group label="Email">
                  <b-form-input
                    v-model.trim="selectedUser.email"
                    autocomplete="off"
                    type="email"
                  />
                  <small
                    v-if="selectedUserErrors.email"
                    class="text-danger"
                  >
                    {{ selectedUserErrors.email }}
                  </small>
                </b-form-group>
                <b-form-group :label="$t('phone')">
                  <b-form-input
                    v-model="selectedUser.phone"
                    autocomplete="off"
                    type="tel"
                    inputmode="numeric"
                    maxlength="10"
                    @input="sanitizeSelectedUserPhone"
                  />
                  <small
                    v-if="selectedUserErrors.phone"
                    class="text-danger"
                  >
                    {{ selectedUserErrors.phone }}
                  </small>
                </b-form-group>
                <b-form-group
                  v-if="selectedUser.role_name === 'customer'"
                  :label="$t('companyName')"
                >
                  <b-form-input
                    v-model.trim="selectedUser.company_name"
                    autocomplete="off"
                    type="text"
                  />
                </b-form-group>
                <b-form-group :label="$t('role')">
                  <b-form-input
                    disabled
                    :value="selectedUser.role_name | roleName"
                    autocomplete="off"
                    type="text"
                  />
                </b-form-group>
                <b-button
                  v-if="selectedUser.role_name === 'supplier_admin'"
                  v-b-modal.confirmChangeRole
                  variant="warning"
                  class="w-100 mr-1"
                >
                  <span class="align-middle">{{
                    $t('changeSupplierToCustomer')
                  }}</span>
                </b-button>
                <div class="d-flex justify-content-end mt-2">
                  <b-button
                    variant="outline-secondary"
                    class="mr-1"
                    @click="$bvModal.hide(`edit-user-${data.item.id}`)"
                  >
                    Cancelar
                  </b-button>
                  <b-button
                    variant="primary"
                    @click="handleEditUser()"
                  >
                    Guardar
                  </b-button>
                </div>
              </b-form>
            </b-modal>
          </b-dropdown>
        </template>
      </b-table>
    </b-overlay>
    <confirmation-modal
      :id="`confirmChangeRole`"
      :variant="'danger'"
      @confirm="changeSuppierRole()"
    >
      <template v-slot:content>
        {{ $t('changeSupplierToCustomerConfirmation') }}
      </template>
    </confirmation-modal>
    <b-pagination
      class="mt-1 ml-1"
      :value="pagination.page"
      :total-rows="pagination.total_objects"
      :per-page="pagination.per_page"
      @change="handlePageChange($event)"
    />
  </b-card>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

export default {
  components: { ConfirmationModal },
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    pagination: {
      type: Object,
      default: () => [],
    },
  },
  data() {
    return {
      selectedUser: {},
      selectedUserErrors: {},
      loading: false,
      fields: [
        {
          key: 'name',
          label: 'Nombre',
        },
        {
          key: 'company_name',
          label: 'Empresa',
        },
        {
          key: 'email',
          label: 'Email',
        },
        {
          key: 'phone',
          label: 'Telefono',
        },
        {
          key: 'role_name',
          label: 'Rol',
        },
        {
          key: 'scope',
          label: 'Pertenece a',
        },
        {
          key: 'created_at',
          label: 'fecha de alta',
        },
        {
          key: 'tools',
          label: '',
        },
      ],
    }
  },

  methods: {
    ...mapMutations('users', {
      changePage: 'CHANGE_PAGE',
    }),
    ...mapActions('users', [
      'fetchUsers',
      'editUser',
      'deactivateUser',
      'deleteUser',
      'changeSupplierToUser',
    ]),
    baseFetchParams(extraParams = {}) {
      const params = {
        by_active_status: true,
        ...extraParams,
      }

      if (this.searchQuery) {
        params.q = this.searchQuery
      }

      return params
    },
    uniqueScopedRoles(user) {
      const scopedRoles = Array.isArray(user.scoped_roles) ? user.scoped_roles : []
      const seen = new Set()

      return scopedRoles.filter(scope => {
        const key = `${scope.role_resource_type}-${scope.role_resource_id}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
    deactivate(user) {
      this.deactivateUser(user).then(() => {
        this.fetchUsers(this.baseFetchParams())
      })
    },
    async removeUser(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${user.name} dejará de tener acceso y sus datos personales se borran. Su empresa y su historial no cambian.`,
        {
          title: '¿Eliminar este usuario?',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return
      try {
        await this.deleteUser(user.id)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Usuario eliminado', icon: 'CheckCircleIcon', variant: 'success' },
        })
        this.fetchUsers(this.baseFetchParams())
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo eliminar',
            text: error?.response?.data?.messages?.[0] || 'Inténtalo de nuevo.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    handlePageChange(page) {
      this.loading = true
      this.changePage(page)
      this.fetchUsers(this.baseFetchParams({
        meta: { pagination: { page } },
      })).then(() => {
        this.loading = false
      })
    },

    openEditUser(user) {
      this.selectedUser = {
        ...user,
        email: String(user.email || '').trim().toLowerCase(),
        phone: String(user.phone || user.customer?.phone || '').replace(/\D/g, '').slice(0, 10),
        company_name: String(user.customer?.company_name || '').trim(),
      }
      this.selectedUserErrors = {}
    },
    sanitizeSelectedUserPhone(value) {
      this.selectedUser.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
      if (this.selectedUserErrors.phone) {
        this.selectedUserErrors = {
          ...this.selectedUserErrors,
          phone: '',
        }
      }
    },
    validateSelectedUser() {
      const errors = {}
      const normalizedName = String(this.selectedUser.name || '').trim()
      const normalizedEmail = String(this.selectedUser.email || '').trim().toLowerCase()
      const normalizedPhone = String(this.selectedUser.phone || '').replace(/\D/g, '')
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!normalizedName) {
        errors.name = 'El nombre es obligatorio'
      }

      if (!normalizedEmail) {
        errors.email = 'El correo es obligatorio'
      } else if (!emailPattern.test(normalizedEmail)) {
        errors.email = 'Ingresa un correo válido'
      }

      if (normalizedPhone && normalizedPhone.length !== 10) {
        errors.phone = 'El teléfono debe tener 10 dígitos'
      }

      if (this.selectedUser.customer && !normalizedPhone) {
        errors.phone = 'El teléfono es obligatorio para clientes'
      }

      this.selectedUserErrors = errors
      return Object.keys(errors).length === 0
    },
    handleEditUser() {
      if (!this.validateSelectedUser()) return

      this.loading = true
      this.editUser({
        id: this.selectedUser.id,
        name: String(this.selectedUser.name || '').trim(),
        email: String(this.selectedUser.email || '').trim().toLowerCase(),
        phone: String(this.selectedUser.phone || '').replace(/\D/g, ''),
        company_name: String(this.selectedUser.company_name || '').trim(),
      }).then(() => this.fetchUsers(this.baseFetchParams()).then(() => {
        this.$bvModal.hide(`edit-user-${this.selectedUser.id}`)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Usuario actualizado',
            text: 'Los datos se guardaron correctamente.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })).catch(error => {
        const messages = error.response?.data?.messages
          || error.data?.messages
          || error.response?.data?.errors
          || error.data?.errors
          || ['No fue posible actualizar el usuario']
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: Array.isArray(messages) ? messages.join(', ') : messages,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.loading = false
      })
    },
    changeSuppierRole() {
      this.changeSupplierToUser(this.selectedUser.id).then(() => {
        this.$bvModal.hide(`edit-user-${this.selectedUser.id}`)
        this.fetchUsers(this.baseFetchParams()).then(() => {
          this.loading = false
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
